import useModalTrigger from 'magik-react-hooks/useModalTrigger'
import { Link } from 'react-router-dom'
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import Header from '../../components/Header'
import Page from '../../components/Page'
import Paginator from '../../components/Paginator'
import Sorter, { SortControl } from '../../components/Sorter'
import StickyTable from '../../components/StickyTable'
import { useQsFilters } from '../../hooks/filters'
import { useAdminedUsers, useDeleteAdminedUser } from '../../hooks/users'
import { AdminedUser, User } from '../../types'
import { format as formatDate } from 'date-fns'
import { useAuthUser } from 'use-eazy-auth'

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get('page') ?? 1),
  ordering: params.get('ordering') ?? '',
  search: params.get('search') ?? '',
})

export default function UsersList() {
  const { user: authUser } = useAuthUser<User>()
  const { filters, uiFilters, setFilters, setFiltersDebounced } =
    useQsFilters(initFilters)
  const { data } = useAdminedUsers(filters)
  const deleteUser = useDeleteAdminedUser()
  const [modalRm, modalRmActions] = useModalTrigger<AdminedUser>()

  return (
    <Page padded>
      <Header>
        <div className="d-flex align-items-end">
          <h2 className="m-0">Utenti</h2>
          <div className="counter ms-2 mb-1">
            <small>
              {data!.count}
              {' / '}
              {data!.total_count}
            </small>
          </div>
        </div>
        <Link className="btn btn-primary" to="/users/new">
          <i className="bi bi-plus-circle me-2"></i> Aggiungi
        </Link>
      </Header>
      <div className="d-flex justify-content-between mt-3 mb-3 align-items-center">
        <input
          placeholder="Cerca"
          className="form-control w-auto"
          type="text"
          value={uiFilters.search}
          onChange={(e) =>
            setFiltersDebounced({ search: e.target.value, page: 1 })
          }
        />
      </div>
      <StickyTable className="flex-1">
        <Sorter
          value={uiFilters.ordering}
          onSortChange={(ordering) =>
            setFilters({
              ordering,
              page: 1,
            })
          }
        >
          <thead>
            <tr>
              <th>#</th>
              <th>
                <div className="d-flex align-items-center">
                  Nome
                  <SortControl field={'name'} />
                </div>
              </th>
              <th>
                <div className="d-flex align-items-center">
                  E-mail
                  <SortControl field={'email'} />
                </div>
              </th>
              <th>
                <div className="d-flex align-items-center">
                  Attivo
                  <SortControl field={'is_active'} />
                </div>
              </th>
              <th>
                <div className="d-flex align-items-center">
                  Staff
                  <SortControl field={'is_staff'} />
                </div>
              </th>
              {authUser!.is_superuser && (
                <th>
                  <div className="d-flex align-items-center">
                    Superuser
                    <SortControl field={'is_superuser'} />
                  </div>
                </th>
              )}
              <th>
                <div className="d-flex align-items-center">
                  Ultimo Accesso
                  <SortControl field={'last_login'} />
                </div>
              </th>
              <th></th>
            </tr>
          </thead>
        </Sorter>
        <tbody>
          {data!.results.map((user) => (
            <tr key={user.id}>
              <td>{user.id}</td>
              <td>
                <Link to={`/users/${user.id}`}>{user.name}</Link>
              </td>
              <td>{user.email}</td>
              <td>
                {user.is_active && (
                  <i className="bi bi-check-circle-fill text-success"></i>
                )}
              </td>
              <td>
                {user.is_staff && (
                  <i className="bi bi-check-circle-fill text-success"></i>
                )}
              </td>
              {authUser!.is_superuser && (
                <td>
                  {user.is_superuser && (
                    <i className="bi bi-check-circle-fill text-success"></i>
                  )}
                </td>
              )}
              <td className="text-nowrap">
                {user.last_login &&
                  formatDate(new Date(user.last_login), 'yyyy-MM-dd HH:mm')}
              </td>
              <td>
                <i
                  onClick={() => modalRmActions.open(user)}
                  className="bi cursor-pointer text-danger bi-trash3-fill"
                ></i>
              </td>
            </tr>
          ))}
        </tbody>
      </StickyTable>
      <Modal centered isOpen={modalRm.isOpen} toggle={modalRmActions.toggle}>
        <ModalHeader toggle={modalRmActions.toggle}>Elimina</ModalHeader>
        <ModalBody>
          {modalRm.value && (
            <div className="pt-3 pb-3">
              Sei sicuro di voler eliminare l'utente {modalRm.value.name} ?
            </div>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <button
            onClick={modalRmActions.toggle}
            className="btn btn-outline-primary"
          >
            Annulla
          </button>
          <button
            className="btn  btn-danger"
            disabled={deleteUser.isLoading}
            onClick={async () => {
              await deleteUser.mutateAsync(modalRm.value!.id)
              modalRmActions.close()
            }}
          >
            <i className="bi bi-trash3-fill"></i> Elimina
          </button>
        </ModalFooter>
      </Modal>
      <div className="position-relative">
        <Paginator
          count={data!.count}
          currentPage={uiFilters.page}
          goToPage={(page) => setFilters({ page })}
        />
      </div>
    </Page>
  )
}
